import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardBody, Col, Input } from 'reactstrap';
import './TravelRequestOptions.scss';
import moment from 'moment';
import { toFixedTwoDecimal } from '../../../utils/number.util';

function FlightOptions({
  requestOptions,
  onSelectFlightOption,
  showTitle = false,
  status = '',
  isView = false,
}) {
  // Retrieve Details
  const { headerDetails, flightOptions, flight2Options, flight3Options } =
    requestOptions || {};
  // Selected Flight Option State
  const [selectedFlightOption, setSelectedFlightOption] = useState(
    requestOptions?.headerDetails?.Selected_Option_Flight || null,
  );
  // Check for empty array
  const isArrayNotEmpty = arr => Array.isArray(arr) && arr.length > 0;
  // Gather all flight options
  const allFlightOptions = [flightOptions, flight2Options, flight3Options];

  // Gather all quote values
  const quotes = [
    { value: headerDetails?.Option1_Flight_QuoteValue },
    { value: headerDetails?.Option2_Flight_QuoteValue },
    { value: headerDetails?.Option3_Flight_QuoteValue },
  ];

  // Get quote with lowest quote value
  let minIndex = -1;
  let minValue = Infinity;

  quotes.forEach((q, i) => {
    if (allFlightOptions[i]?.length) {
      const val = Number(q?.value);
      if (val < minValue) {
        minValue = val;
        minIndex = i;
      }
    }
  });

  // Set selected flight option
  const handleSelectedFlightOption = useCallback(
    optionIndex => {
      setSelectedFlightOption(optionIndex);
      // Check if it's the quote with the lowest quote value
      const isLowest = optionIndex === minIndex + 1;
      if (onSelectFlightOption) {
        onSelectFlightOption(optionIndex, isLowest);
      }
    },
    [minIndex, onSelectFlightOption],
  );

  useEffect(() => {
    if (status === 'Pending for Decision') {
      handleSelectedFlightOption(minIndex + 1);
    }
  }, [minIndex, handleSelectedFlightOption, status]);

  const getBackgroundColor = index => {
    if (index === minIndex + 1) {
      return '#ECFDF3';
    }
    return '#f0f9ff';
  };

  const getDefaultBackgroundColor = index => {
    if (index === minIndex + 1) {
      return '#ECFDF3';
    }
    return '#ffffff';
  };

  const displayValue = (v, fallback = '-') => {
    if (v == null) return fallback;
    if (typeof v === 'string') {
      const t = v.trim();
      return t === '' ? fallback : t;
    }
    return v;
  };

  return (
    <>
      {/* Flight Selection */}
      <>
        {showTitle && (
          <h6 className="text-sm fw-semibold">Flight Booking Option</h6>
        )}
        <div className="flight-options-container">
          {allFlightOptions.map((option, index) => {
            const noRecord = Object.entries(option?.[0] || {}).some(
              ([key, value]) => {
                if (key === 'TravelClient') return false;
                return value === null || value === undefined || value === '';
              },
            );

            const seq = index + 1;

            return (
              <Card
                className="trd-flight-selection cursor-pointer"
                onClick={() => !isView && handleSelectedFlightOption(index + 1)}
                style={{
                  pointerEvents: noRecord ? 'none' : '',
                  background:
                    selectedFlightOption === index + 1
                      ? getBackgroundColor(index + 1)
                      : getDefaultBackgroundColor(index + 1),
                  borderColor: minIndex === index ? '#47CD89' : '#98a2b3',
                }}
                key={index}
              >
                <CardBody>
                  <div className="d-flex flex-row align-items-center gap-2 mb-3">
                    <div>
                      <Input
                        type="radio"
                        name="flight-selection"
                        disabled={noRecord}
                        checked={selectedFlightOption === index + 1}
                        onChange={() =>
                          !isView && handleSelectedFlightOption(index + 1)
                        }
                      />
                    </div>
                    <div className="trd-flight-selection-label">
                      Flight Option - {seq}
                    </div>
                  </div>
                  {isArrayNotEmpty(option) ? (
                    <>
                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex flex-row align-items-center justify-content-between">
                          <strong>Quote Value</strong>
                          <div>
                            {headerDetails?.[`Option${seq}_Flight_Curr`]}
                            {toFixedTwoDecimal(
                              headerDetails?.[`Option${seq}_Flight_QuoteValue`],
                            )}
                          </div>
                        </div>
                        <div className="d-flex flex-row align-items-center justify-content-between">
                          <strong>Refund</strong>
                          <div>
                            {headerDetails?.[`Option${seq}_Flight_Curr`]}
                            {headerDetails?.[`Option${seq}_Flight_Refund`]}
                          </div>
                        </div>
                        <div className="d-flex flex-row align-items-center justify-content-between">
                          <strong>Rebooking</strong>
                          <div>
                            {displayValue(
                              headerDetails?.[`Option${seq}_Flight_Rebooking`],
                            )}
                          </div>
                        </div>
                        <div className="d-flex flex-row align-items-start justify-content-between">
                          <Col>
                            <strong>Terms</strong>
                          </Col>
                          <Col className="d-flex justify-content-end">
                            <div>
                              <p className="trd-terms">
                                {displayValue(
                                  headerDetails?.[`Option${seq}_Flight_Terms`],
                                )}
                              </p>
                            </div>
                          </Col>
                        </div>
                      </div>
                      <div className="trd-flight-separator-header" />
                      {option?.map((citem, cindex) => (
                        <>
                          <div
                            className="d-flex flex-column gap-2"
                            key={cindex}
                          >
                            <div className="d-flex flex-row align-items-center justify-content-between">
                              <strong>Airline</strong>
                              <div>{citem?.Airline_Description?.trim()}</div>
                            </div>
                            <div className="d-flex flex-row align-items-center justify-content-between">
                              <strong>From</strong>
                              <div>{citem?.TravelFrom_Description?.trim()}</div>
                            </div>
                            <div className="d-flex flex-row align-items-center justify-content-between">
                              <strong>To</strong>
                              <div>{citem?.TravelTo_Description?.trim()}</div>
                            </div>
                            <div className="d-flex flex-row align-items-center justify-content-between">
                              <strong>Time</strong>
                              <div>
                                {`${
                                  citem?.TravelDate?.trim()
                                    ? moment(citem?.TravelDate?.trim()).format(
                                        'L',
                                      )
                                    : 'N/A'
                                }, ${
                                  citem?.TravelTime
                                    ? moment(
                                        citem?.TravelTime,
                                        'HH:mm:ss',
                                      ).format('hh:mm A')
                                    : '-'
                                }`}
                              </div>
                            </div>
                          </div>
                          {cindex !== (option?.length ?? 1) - 1 && (
                            <div className="trd-flight-separator mt-2" />
                          )}
                        </>
                      ))}
                    </>
                  ) : (
                    <span>No flight option found.</span>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      </>
    </>
  );
}

export default FlightOptions;
